/**
 * Job types (piece 29, *The field side: job types that pre-fill*; T390, T1710).
 * 🔒 A job type is a template the owner curates: picking one on a Draft job
 * writes a labor line per task with its level and sub-skills, a planned part per
 * part and the type's checklist, and its length is the booking window's default.
 * 🔒 Everything it fills is a copy, editable on the job, and a type is applied
 * once: changing it afterwards records the new type and leaves the job as it
 * stands. 🔨 A type is deactivated and never deleted, and none is seeded (the
 * owner's answer of 2026-10-10, 8b).
 */

/** The Settings page, under Settings › Work. */
export const JOB_TYPES_ROUTE = '/settings/work/job-types';
/** The editor's list and its add, relative to the API root. */
export const JOB_TYPES_PATH = '/work/job-types';
/** The active units alone, which the Add Planned Part window reads without the whole list (T2390). */
export const JOB_TYPE_UNITS_PATH = `${JOB_TYPES_PATH}/units`;

/** One type's read and its edit. */
export function jobTypePath(id: string): string {
  return `${JOB_TYPES_PATH}/${encodeURIComponent(id)}`;
}

/** One type's deactivation and its reactivation. */
export function jobTypeActivePath(id: string): string {
  return `${jobTypePath(id)}/active`;
}

/** Picking a type on a Draft job, gated as the job's own edits. */
export function workOrderJobTypePath(workOrderId: string): string {
  return `/work-orders/${encodeURIComponent(workOrderId)}/job-type`;
}

/** A job's planned parts: the add, and one part's removal. */
export function workOrderPlannedPartsPath(workOrderId: string, partId?: string): string {
  const base = `/work-orders/${encodeURIComponent(workOrderId)}/planned-parts`;
  return partId ? `${base}/${encodeURIComponent(partId)}` : base;
}

export interface JobTypeTaskRow {
  id: string;
  description: string;
  requiredSkillLevelId: string;
  requiredSkillLevelName: string;
  subSkills: { id: string; name: string; skillName: string }[];
}

export interface JobTypePartRow {
  id: string;
  itemId: string;
  sku: string;
  itemName: string;
  quantity: number;
  unitId: string;
  unitName: string;
}

export interface JobTypeRow {
  id: string;
  version: number;
  name: string;
  description: string | null;
  estimatedMinutes: number | null;
  checklistTemplateId: string | null;
  checklistTemplateName: string | null;
  sortOrder: number;
  isActive: boolean;
  tasks: JobTypeTaskRow[];
  parts: JobTypePartRow[];
  /** 🔒 Decision 2282: the certifications the type's checklist template asks for, derived from the training articles its items cite. */
  training: string[];
}

/** What the editor and the two job windows read: every type, and the pickers' choices. */
export interface JobTypesAnswer {
  rows: JobTypeRow[];
  /** 🔒 Decision 2282: each template with the training it asks for, so the window names it as the template is chosen. */
  templates: { id: string; name: string; training: string[] }[];
  levels: { id: string; name: string }[];
  subSkills: { id: string; name: string; skillName: string }[];
  /** Every active unit with its family, so a part offers the units of its Item's own family. */
  units: JobTypeUnitsAnswer['units'];
}

/** The units alone, each with its family. */
export interface JobTypeUnitsAnswer {
  units: { id: string; unitName: string; familyId: string }[];
}

export interface JobTypeTaskDraft {
  description: string;
  requiredSkillLevelId: string;
  subSkillIds: string[];
}

export interface JobTypePartDraft {
  itemId: string;
  /** Shown beside the picker; the server reads the id alone. */
  itemLabel?: string;
  quantity: number | null;
  unitId: string;
}

/** What the editor's window sends; the tasks and the parts replace the type's own whole. */
export interface JobTypeDraft {
  name: string;
  description: string;
  estimatedMinutes: number | null;
  checklistTemplateId: string | null;
  sortOrder?: number;
  tasks: JobTypeTaskDraft[];
  parts: JobTypePartDraft[];
}

/** What picking a type on a job sends: the type, and the job's version the window read. */
export interface ApplyJobTypeInput {
  jobTypeId: string;
  version?: number | null;
}

/** What a planned part's add sends. */
export interface PlannedPartDraft {
  itemId: string;
  quantity: number | null;
  unitId: string | null;
}

/** A planned part as the Work Order's page draws it. */
export interface PlannedPartRow {
  id: string;
  itemId: string;
  sku: string;
  itemName: string;
  quantity: number;
  unitId: string;
  unitName: string;
  /** Whether the job's type pre-filled it; false on a part a dispatcher typed. */
  fromJobType: boolean;
}

/** The largest estimated length the column holds, in minutes (a 32-bit whole number). */
const JOB_TYPE_MAX_MINUTES = 2_147_483_647;
/** The largest part quantity the column holds: ten whole digits beside four decimals. */
export const JOB_TYPE_MAX_QUANTITY = 9_999_999_999;

/**
 * The sentences a draft is refused in, the window's and the server's alike.
 * 🔨 A body of the wrong shape (a task with no description, a quantity or minutes out of range) is read here as a
 * sentence and never reaches the database to answer 500 (T2390).
 */
export function jobTypeProblems(draft: JobTypeDraft): string[] {
  const problems: string[] = [];
  if (typeof draft.name !== 'string' || !draft.name.trim()) problems.push('A job type has a name.');
  if (draft.estimatedMinutes !== null && !(Number.isInteger(draft.estimatedMinutes) && draft.estimatedMinutes > 0)) problems.push('The estimated length is a whole number of minutes above zero.');
  else if (draft.estimatedMinutes !== null && draft.estimatedMinutes > JOB_TYPE_MAX_MINUTES) problems.push(`The estimated length is at most ${JOB_TYPE_MAX_MINUTES} minutes.`);
  draft.tasks.forEach((task, index) => {
    if (typeof task?.description !== 'string' || !task.description.trim()) problems.push(`Task ${index + 1} says what the work is.`);
    if (!task?.requiredSkillLevelId) problems.push(`Task ${index + 1} names the skill level it demands.`);
  });
  draft.parts.forEach((part, index) => {
    if (!part?.itemId) problems.push(`Part ${index + 1} names a catalog Item.`);
    if (!(typeof part?.quantity === 'number' && part.quantity > 0)) problems.push(`Part ${index + 1} carries a quantity above zero.`);
    else if (!(part.quantity <= JOB_TYPE_MAX_QUANTITY)) problems.push(`Part ${index + 1} carries a quantity of at most ${JOB_TYPE_MAX_QUANTITY}.`);
    if (!part?.unitId) problems.push(`Part ${index + 1} names its unit.`);
  });
  return problems;
}

/** The estimated length as the list reads it: hours and minutes, or none. */
export function jobTypeLengthText(minutes: number | null): string | null {
  if (minutes === null || minutes <= 0) return null;
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  if (hours === 0) return `${rest} min`;
  return rest === 0 ? `${hours} h` : `${hours} h ${rest} min`;
}
